"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

type CcFoldVideoProps = {
  src: string;
  srcSmall: string;
  poster: string;
  alt: string;
};

/**
 * Background clip for a services-fold scene. The still frame shows while the
 * card is closed; the clip is fetched the first time the card opens (hover,
 * focus or `data-active` on touch screens), plays while open and pauses when
 * it closes. Phones get the smaller file. With reduced motion the still frame
 * stays.
 */
export function CcFoldVideo({ src, srcSmall, poster, alt }: CcFoldVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const reduced = useCcPrefersReducedMotion();

  useEffect(() => {
    const video = ref.current;
    const card = video?.closest<HTMLElement>(".cc-hsf-card");
    if (!video || !card) return;
    if (reduced) {
      video.pause();
      return;
    }
    let frame: number | null = null;
    const ready = () => { video.dataset.ready = "true"; };
    video.addEventListener("loadeddata", ready);
    const controlled = card.closest(".cc-hsf") !== null;
    const isOpen = () =>
      card.dataset.active === "true" || (!controlled && card.matches(":hover, :focus-within"));
    const sync = () => {
      if (!isOpen()) {
        video.pause();
        return;
      }
      if (!video.getAttribute("src")) {
        video.src = window.matchMedia("(max-width: 767px)").matches
          ? srcSmall
          : src;
      }
      void video.play().catch(() => {});
    };
    // Pointer leave fires before :hover clears, so check on the next frame.
    const later = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { frame = null; sync(); });
    };
    const events = ["pointerenter", "pointerleave", "focusin", "focusout"];
    events.forEach((name) => card.addEventListener(name, later));
    const observer = new MutationObserver(sync);
    observer.observe(card, { attributes: true, attributeFilter: ["data-active"] });
    sync();
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      video.removeEventListener("loadeddata", ready);
      video.pause();
      events.forEach((name) => card.removeEventListener(name, later));
      observer.disconnect();
    };
  }, [src, srcSmall, reduced]);

  return (
    <>
      <Image src={poster} alt={alt} fill sizes="(min-width: 1024px) 400px, 92vw" />
      <video
        ref={ref}
        className="cc-hsf-photo__video"
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
      />
    </>
  );
}
